import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getTelemetryClient, trackAppOpened, trackScreenViewed } from "../../lib/telemetry";

/**
 * usage-event-pipeline (task 4.9): mounted once in App.tsx.
 * - `app_opened` fires once per page load.
 * - `screen_viewed` fires on the initial route and every client-side
 *   navigation (`useLocation()` change).
 * - The queue flushes on page hide so a batched event never gets stranded
 *   by an abrupt tab close/navigation away.
 */
export function TelemetryRouteTracker(): null {
  const location = useLocation();
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    trackAppOpened();
  }, []);

  useEffect(() => {
    trackScreenViewed(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const flushOnHide = () => {
      if (document.visibilityState === "hidden") {
        void getTelemetryClient().flush();
      }
    };
    document.addEventListener("visibilitychange", flushOnHide);
    window.addEventListener("pagehide", flushOnHide);
    return () => {
      document.removeEventListener("visibilitychange", flushOnHide);
      window.removeEventListener("pagehide", flushOnHide);
    };
  }, []);

  return null;
}
